/**
 * Check if the target element is occluded (not visible) in the viewport.
 *
 * An element is considered occluded if all four of its inner corner points
 * are either outside the viewport or covered by other elements.
 *
 * @param targetElement - The DOM element to check for occlusion.
 * @returns True if the element is occluded, false otherwise.
 * */
export function checkOcclusion(targetElement: Element) {
    const rect = targetElement.getBoundingClientRect();

    const viewportWidth = document.documentElement?.clientWidth ?? window.innerWidth;
    const viewportHeight = document.documentElement?.clientHeight ?? window.innerHeight;

    // Use a small inset to avoid sampling exactly on the border where rounding may fluctuate
    const insetX = Math.min(4, Math.max(1, rect.width / 10));
    const insetY = Math.min(4, Math.max(1, rect.height / 10));

    // Four inner-corner points (top-left, top-right, bottom-left, bottom-right)
    const points: Array<[number, number]> = [
        [rect.left + insetX, rect.top + insetY],
        [rect.right - insetX, rect.top + insetY],
        [rect.left + insetX, rect.bottom - insetY],
        [rect.right - insetX, rect.bottom - insetY],
    ];

    // If any point is visible (inside viewport and unobstructed), do NOT hide
    for (const [x, y] of points) {
        const insideViewport = x >= 0 && y >= 0 && x <= viewportWidth && y <= viewportHeight;
        if (!insideViewport) {
            continue;
        }
        const elementAtPoint = document.elementFromPoint(x, y);
        const unobstructed = elementAtPoint === targetElement || targetElement.contains(elementAtPoint);
        if (unobstructed) {
            return false; // at least one point visible -> not hidden
        }
    }

    // All points are either outside the viewport or occluded -> hidden
    return true;
}
